/**
 * Frontend Error Handler Utility
 *
 * Provides centralized error handling for API responses and token expiration detection.
 */

export interface ApiError {
  error: string;
  code?: string;
  details?: string;
}

export class ErrorHandler {
  /**
   * Extract user-friendly error message from API error response
   *
   * @param error - The error object from axios or other API calls
   * @returns User-friendly error message in German
   */
  static handleApiError(error: any): string {
    // Check for API error message in response
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    // Check for network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
    }

    // Check for connection refused
    if (error.code === 'ECONNREFUSED' || error.message?.includes('ERR_CONNECTION_REFUSED')) {
      return 'Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es spaeter erneut.';
    }

    // Check for timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Zeitüberschreitung. Bitte versuchen Sie es erneut.';
    }

    // Fallback to generic error
    return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  /**
   * Check if error is due to expired/invalidated token
   *
   * @param error - The error object from axios or other API calls
   * @returns True if token is expired or invalidated
   */
  static isTokenExpired(error: any): boolean {
    const code = error.response?.data?.code;
    return code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALIDATED' || code === 'TOKEN_INVALID';
  }

  /**
   * Check if error is an authentication error (401 or 403)
   *
   * @param error - The error object from axios or other API calls
   * @returns True if error is authentication-related
   */
  static isAuthError(error: any): boolean {
    return error.response?.status === 401 || error.response?.status === 403;
  }
}
